import type { Dispatch, SetStateAction } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Box from '@/components/UI/Box';
import * as Menu from '@/components/UI/Menu';
import palette from '@/utils/palette';
import pickAvatar from '@/utils/pickAvatar';
import * as Header from '../Custom/Header';
import Icon from '../Unistyles/Icon';
import type { EntityListValues } from './EntityList.types';

export default function EntityListMenu({
  name,
  index,
  isMenuOpen,
  setMenuIndex,
}: {
  name: string;
  index: number;
  isMenuOpen: boolean;
  setMenuIndex: Dispatch<SetStateAction<number | null>>;
}) {
  const { setValue, control } = useFormContext<EntityListValues>();
  const localAvatarUrl = useWatch({
    name: `${name}.${index}.localAvatarUrl`,
    control,
  });

  return (
    <Menu.Root
      open={isMenuOpen}
      onOpenChange={(nextOpen) => {
        setMenuIndex(nextOpen ? index : null);
      }}
    >
      <Menu.Trigger>
        {(triggerProps) => (
          <Header.Button
            {...triggerProps}
            accessibilityLabel="Avatar options"
            tabIndex={-1}
          >
            <Icon name="ellipsis-horizontal-outline" />
          </Header.Button>
        )}
      </Menu.Trigger>
      <Menu.Content x="right" y="bottom">
        <Menu.Label>Choose Color</Menu.Label>
        <Box style={styles.paletteGrid}>
          {palette.map((color) => (
            <Pressable
              accessibilityLabel={`Choose ${color}`}
              key={color}
              onPress={() => {
                setValue(`${name}.${index}.color`, color);
              }}
              style={styles.paletteSwatch({ color })}
            />
          ))}
        </Box>
        {!localAvatarUrl ? (
          <Menu.Item
            onSelect={async () => {
              const image = await pickAvatar();

              if (image?.uri) {
                setValue(`${name}.${index}.localAvatarUrl`, image.uri);
              }
            }}
          >
            <Icon name="camera-outline" />
            <Menu.ItemTitle>Upload Avatar</Menu.ItemTitle>
          </Menu.Item>
        ) : (
          <Menu.Item
            onSelect={() => {
              setValue(`${name}.${index}.localAvatarUrl`, undefined);
            }}
          >
            <Icon name="trash-outline" />
            <Menu.ItemTitle>Remove Avatar</Menu.ItemTitle>
          </Menu.Item>
        )}
      </Menu.Content>
    </Menu.Root>
  );
}

const styles = StyleSheet.create((theme) => ({
  paletteGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.space('sm'),
    maxWidth: theme.size(64),
    paddingHorizontal: theme.size(3),
    paddingVertical: theme.size(2),
  },
  paletteSwatch: ({ color }: { color: string }) => ({
    backgroundColor: color,
    borderRadius: theme.borderRadius.md,
    height: theme.size(7),
    width: theme.size(7),
  }),
}));
